/**
 * The desktop frame: theme root → FULL-BLEED column → sticky 58px top nav →
 * screen switch, plus every desktop overlay mount point.
 *
 * Ported from `design/Games Tonight.dc.html` lines 26, 74-98, 790-1156
 * (INVENTORY-DESKTOP §1, §2, §4). `App.tsx` renders this or `MobileApp`
 * depending on `useBreakpoint()`; exactly one is mounted at a time.
 *
 * FRAME RULES the screen lanes must not re-litigate:
 *  - THERE IS NO MAX-WIDTH COLUMN. Mobile's `max-width:430px; margin:0 auto`
 *    with 1px side borders is gone (§1.1); content measure is enforced PER
 *    SCREEN (Detail 1180, Friends 1180, Profile 860) and never on the shell.
 *  - `login` and `reset` render WITHOUT the top nav. The gate is
 *    `headerVM().authed`.
 *  - `round`, `detail`, `interests` and `goty` are FULL SCREEN and render
 *    without it too, each carrying its own top bar with the way out. All gate
 *    on `hdrOn`, which both trees share — the phone hides its header and tab
 *    bar on the same screens. (The last two since DESIGN-CHANGELOG 2026-09-03:
 *    they are doors inside MY GAMES, and their bar is `◂ BACK TO MY GAMES`.)
 *  - The screen switch is a single `screen` field. There is no router.
 *  - Screens take NO props; they read the store and their own selector.
 *  - `Tooltip` and the toast host are mounted here, exactly once, per tree.
 *  - Modals mount here off the ui-slice flags; each modal closes itself.
 *  - The Filter Bar PANEL mounts here too, even though the trigger rail lives
 *    inside five different screens — see `components/FilterBar.tsx` for why
 *    (a `position:fixed` child of a `transform`ed hover tile would be clipped).
 *
 * `.qd-desktop` ON `<html>`: every rule in `styles/desktop.css` is scoped to it,
 * so the mobile tree can never inherit the 11px scrollbars, the 32px/18px
 * `.gt-dual` or the twelve `:hover` classes. It is added on mount and removed on
 * unmount, which includes the breakpoint crossing.
 *
 * Z-LADDER (§1.5, and the comment block in `styles/desktop.css`):
 *   nav 40 · filter panel 70 · modals 80 · scanlines 90 · toasts 100 ·
 *   tooltip 200 · drag ghost 9999.
 *
 * Subscription note: like `MobileApp`, this component subscribes to the WHOLE
 * store (`useQd()`) because `headerVM` reads across almost every slice. Screens
 * should subscribe narrowly instead.
 */

import { useEffect } from 'react';

import { Tooltip } from '../components/Tooltip.tsx';
import { accentVars } from '../components/theme.ts';
import type { Accent } from '../components/theme.ts';
import { usePageScroll } from '../lib/scroll.ts';
import { overlayOpen } from '../store/history.ts';
import { useQd } from '../store/index.ts';
import { takeResumeScroll } from '../store/resume.ts';
import { headerVM } from '../store/selectors.ts';
import { FilterPanel } from './components/FilterBar.tsx';
import { DesktopToastHost } from './components/ToastHost.tsx';
import { endDesktopDrag } from './dragHtml5.ts';
import CustomColorModal from './modals/CustomColorModal.tsx';
import GotyPickModal from './modals/GotyPickModal.tsx';
import OwnerModals from './modals/OwnerModals.tsx';
import PlayerCountModal from './modals/PlayerCountModal.tsx';
import PtAddModal from './modals/PtAddModal.tsx';
import PtAskModal from './modals/PtAskModal.tsx';
import PtBrowseModal from './modals/PtBrowseModal.tsx';
import PtDetailModal from './modals/PtDetailModal.tsx';
import PtSyncModal from './modals/PtSyncModal.tsx';
import PtTrackedModal from './modals/PtTrackedModal.tsx';
import ScoreModal from './modals/ScoreModal.tsx';
import SendModal from './modals/SendModal.tsx';
import TellMeWhenModal from './modals/TellMeWhenModal.tsx';
import AddItModal from './modals/AddItModal.tsx';
import CallItModal from './modals/CallItModal.tsx';
import TrackModal from './modals/TrackModal.tsx';
import WhatsNewModal from './modals/WhatsNewModal.tsx';
import Crew from './screens/Crew.tsx';
import Detail from './screens/Detail.tsx';
import Discover from './screens/Discover.tsx';
import Goty from './screens/Goty.tsx';
import Interests from './screens/Interests.tsx';
import Landing from './screens/Landing.tsx';
import Join from './screens/Join.tsx';
import Ask from './screens/Ask.tsx';
import Backlog from './screens/Backlog.tsx';
import Scores from './screens/Scores.tsx';
import Monitoring from './screens/Monitoring.tsx';
import Manage from './screens/Manage.tsx';
import Member from './screens/Member.tsx';
import Recs from './screens/Recs.tsx';
import Waiting from './screens/Waiting.tsx';
import StartCrew from './screens/StartCrew.tsx';
import Login from './screens/Login.tsx';
import Mine from './screens/Mine.tsx';
import Playtests from './screens/Playtests.tsx';
import Profile from './screens/Profile.tsx';
import Recover from './screens/Recover.tsx';
import Reset from './screens/Reset.tsx';
import Round from './screens/Round.tsx';
import Schedule, { SchedNight } from './screens/Schedule.tsx';
import SchedModals from './modals/SchedModals.tsx';
import Tonight from './screens/Tonight.tsx';
import { RoundHover, RoundJoinCard } from './components/RoundOverlays.tsx';
import { useSearchHotkey } from './searchHotkey.ts';
import TopNav from './TopNav.tsx';
import { resetHoverTiltModule } from './useHoverTilt.ts';
import './styles/desktop.css';

export interface DesktopAppProps {
  /** design-time prop, owned by `App.tsx` (INVENTORY §10) */
  accent: Accent;
  /** design-time prop, owned by `App.tsx` */
  scanlines: boolean;
}

export function DesktopApp({ accent, scanlines }: DesktopAppProps): JSX.Element {
  const s = useQd();
  const h = headerVM(s);

  // CSS scope for `styles/desktop.css`. On `<html>` rather than the app root so
  // it also reaches the page scrollbar and anything appended to `<body>`.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.add('qd-desktop');
    return () => root.classList.remove('qd-desktop');
  }, []);

  // Game detail V2 is the one screen the page does not scroll on — its three
  // columns each own their overflow instead, and that needs a resolved height
  // chain the app's own wrappers do not provide (see `.qd-detail-lock`). Keyed
  // on the screen and torn down on the way out: left behind, it freezes every
  // other screen.
  useEffect(() => {
    if (!h.isDetail) return;
    const root = document.documentElement;
    root.classList.add('qd-detail-lock');
    return () => root.classList.remove('qd-detail-lock');
  }, [h.isDetail]);

  // Teardown for the two imperative subsystems. Both are module singletons, so
  // crossing the breakpoint mid-drag or mid-hover must not orphan a body node
  // or leave an rAF running against unmounted elements (§10.5 risk 9).
  useEffect(
    () => () => {
      endDesktopDrag();
      resetHoverTiltModule();
    },
    [],
  );

  useSearchHotkey(h.authed);

  // Same three rules as the mobile frame, from the same module: screens keep
  // their scroll offset and get it back, a pushed `detail` opens at the top, and
  // an open modal freezes the page under it (here that also means compensating
  // for the 11px scrollbar the freeze removes — see `lib/scroll.ts`).
  usePageScroll(s.screen, s.detailId, overlayOpen(s), takeResumeScroll);

  return (
    <div
      style={{
        // Detail V2 hands its height down instead of growing past the viewport:
        // `100vh` here would make the shell taller than its parent and push the
        // panes' overflow back onto the page. Every other screen is unchanged.
        minHeight: h.isDetail ? 0 : '100vh',
        height: h.isDetail ? '100%' : undefined,
        background: 'var(--gt-bg)',
        color: 'var(--gt-text)',
        fontFamily: 'var(--gt-font-sans)',
        ...accentVars(accent),
      }}
    >
      {h.isLanding ? <Landing /> : null}
        {h.isStartCrew ? <StartCrew /> : null}
        {h.isJoin ? <Join /> : null}
        {h.isAsk ? <Ask /> : null}
        {h.isWaiting ? <Waiting /> : null}
        {h.isLogin ? <Login /> : null}
        {h.isRecover ? <Recover /> : null}
      {h.isReset ? <Reset /> : null}

      {h.authed ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: h.isDetail ? 0 : '100vh',
            height: h.isDetail ? '100%' : undefined,
          }}
        >
          {/* The round page, game detail, and MY GAMES' sub-pages (MY INTERESTS,
              GOTY) are all full screen: no nav while one is up, and each carries
              its own top bar with the way out. */}
          {h.hdrOn ? <TopNav /> : null}

          {/* Screen switch — one `screen` field, no router (§2.1) */}
          {h.isMine ? <Mine /> : null}
          {h.isBacklog ? <Backlog /> : null}
          {h.isScores ? <Scores /> : null}
          {h.isRecs ? <Recs /> : null}
          {h.isMonitoring ? <Monitoring /> : null}
          {h.isManage ? <Manage /> : null}
          {h.isMember ? <Member /> : null}
          {h.isRound ? <Round /> : null}
          {/* SCHEDULE (3071): the month under the nav; a night full screen. */}
          {h.isSchedule ? <Schedule /> : null}
          {h.isSchedDay ? <SchedNight /> : null}
          {h.isDiscover ? <Discover /> : null}
          {h.isTonight ? <Tonight /> : null}
          {h.isInterests ? <Interests /> : null}
          {h.isGoty ? <Goty /> : null}
          {h.isPlaytests ? <Playtests /> : null}
          {h.isDetail ? <Detail /> : null}
          {h.isCrew ? <Crew /> : null}
          {h.isProfile ? <Profile /> : null}
        </div>
      ) : null}

      {/* Scanline overlay — FULL VIEWPORT on desktop (§4 row 9) */}
      {scanlines ? <div className="gt-scanlines-d" /> : null}

      {/* Overlays. Filter panel 70 · everything else 80 (§4). The move sheet
          and the React drag ghost do not exist on desktop: drag replaces the
          first, and an imperative body node at z-9999 replaces the second. */}
      {s.fltOpen ? <FilterPanel /> : null}
      {s.wnOpen ? <WhatsNewModal /> : null}
      {s.ptDet ? <PtDetailModal /> : null}
      {s.ptAddOpen ? <PtAddModal /> : null}
      {s.ptBrowseOpen ? <PtBrowseModal /> : null}
      {s.ptTrackedOpen ? <PtTrackedModal /> : null}
      {s.ptSyncOpen ? <PtSyncModal /> : null}
      {/* Over the detail modal that can raise it — the Escape stack is LIFO. */}
      {s.ptAsk ? <PtAskModal /> : null}
      {s.gotyPickOpen ? <GotyPickModal /> : null}
      {s.capOpen ? <PlayerCountModal /> : null}
      {s.ccOpen ? <CustomColorModal /> : null}
      {s.ownModal ? <OwnerModals /> : null}
      {/* MY GAMES' two dialogs (3060). Last, so the Escape stack and
          `history.ts` agree they sit over whatever opened them. */}
      {s.scId ? <ScoreModal /> : null}
      {s.trkOpen ? <TrackModal /> : null}
      {/* RECOMMEND (3062) — `send` in history.ts, after `track`. */}
      {s.sdId ? <SendModal /> : null}
      {/* TELL ME WHEN (3063) — `wm` in history.ts, after `send`. */}
      {s.wmId ? <TellMeWhenModal /> : null}
      {/* CALL IT (AA phase 5) — `ask` in history.ts, after `wm`. */}
      {s.askId ? <CallItModal /> : null}
      {/* ADD IT (docs/ADD-IT.md) — `add` in history.ts, after `ask`. */}
      {s.addId ? <AddItModal /> : null}
      {/* Rounds (3039): the hover panel over the wheel and the one-shot join card. */}
      <RoundHover />
      <RoundJoinCard />
      {/* SCHEDULE (3071): NOMINATE and the game modal, over a night's page. */}
      <SchedModals />

      <DesktopToastHost />
      {/* mobile mounts the same component at z 110; desktop's `#gt-tip` is 200 */}
      <Tooltip zIndex={200} />
    </div>
  );
}

export default DesktopApp;
